import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, ArrowRight, Loader2, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// PRICING CONFIG (single source of truth — easy to update in one place)
// Median NM ballpark ranges (Albuquerque · Rio Rancho · Santa Fe · Los Lunas · Bernalillo)
// ─────────────────────────────────────────────────────────────────────────────
const SIZES = [
    { id: 's1', label: 'Under 1,500 sq ft' },
    { id: 's2', label: '1,500 – 2,500 sq ft' },
    { id: 's3', label: '2,500 – 4,000 sq ft' },
    { id: 's4', label: '4,000+ sq ft' },
];

const SERVICES = [
    { id: 'v1', label: 'Interior Painting', modalKey: 'interior' },
    { id: 'v2', label: 'Exterior Painting', modalKey: 'exterior' },
    { id: 'v3', label: 'Cabinet Refinishing', modalKey: 'cabinets' },
    { id: 'v4', label: 'Stucco Refinishing', modalKey: 'stucco' },
    { id: 'v5', label: 'Commercial', modalKey: 'commercial', customQuote: true },
];

// Ranges keyed by [serviceId][sizeId]
const PRICING = {
    v1: { // Interior
        s1: { low: 2500, high: 3200 },
        s2: { low: 4200, high: 5100 },
        s3: { low: 6300, high: 7600 },
        s4: { low: 9500, high: 12000 },
    },
    v2: { // Exterior
        s1: { low: 3500, high: 4400 },
        s2: { low: 5300, high: 6600 },
        s3: { low: 7800, high: 9500 },
        s4: { low: 11500, high: 15000 },
    },
    v3: { // Cabinet
        s1: { low: 3200, high: 4200 },
        s2: { low: 4200, high: 5500 },
        s3: { low: 5800, high: 7200 },
        s4: { low: 8000, high: 10000 },
    },
    v4: { // Stucco
        s1: { low: 4500, high: 5500 },
        s2: { low: 6200, high: 7800 },
        s3: { low: 8500, high: 10500 },
        s4: { low: 12500, high: 16000, plus: true },
    },
    // v5 Commercial → custom quote (no numeric range)
};

const INCLUDED = [
    'Professional Surface Preparation',
    'Premium Materials',
    'Experienced Local Painters',
    'Free On-Site Estimate',
];

const LOADING_MESSAGES = [
    'Analyzing your project…',
    'Calculating your estimate…',
    'Comparing regional pricing…',
];

const DISCLAIMER =
    'This estimate is provided as a general pricing guide based on average residential painting costs throughout New Mexico. Final pricing depends on project scope, surface condition, preparation requirements, repairs, paint products, accessibility, architectural complexity, and additional requested services. Every project receives a free on-site estimate before work begins.';

const fmt = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

// ─────────────────────────────────────────────────────────────────────────────

export const EstimateTeaser = () => {
    const [size, setSize] = useState(null);
    const [svc, setSvc] = useState(null);
    const [phase, setPhase] = useState('idle'); // idle | loading | result
    const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
    const navigate = useNavigate();

    // When both are picked, trigger the loading → result animation
    useEffect(() => {
        if (size && svc) {
            setPhase('loading');
            setLoadingMsgIdx(0);
            // Cycle messages every ~450ms during the ~1.35s loading window
            const cycle = setInterval(() => {
                setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length);
            }, 450);
            const done = setTimeout(() => {
                clearInterval(cycle);
                setPhase('result');
            }, 1350);
            return () => {
                clearInterval(cycle);
                clearTimeout(done);
            };
        } else {
            setPhase('idle');
        }
    }, [size, svc]);

    const result = useMemo(() => {
        if (!size || !svc) return null;
        const sz = SIZES.find((s) => s.id === size);
        const sv = SERVICES.find((s) => s.id === svc);
        if (!sz || !sv) return null;
        if (sv.customQuote) {
            return { sz, sv, customQuote: true };
        }
        const range = PRICING[sv.id]?.[sz.id];
        if (!range) return null;
        return { sz, sv, range };
    }, [size, svc]);

    // Persist last estimate
    useEffect(() => {
        if (result && phase === 'result') {
            try {
                localStorage.setItem('msd:lastEstimate', JSON.stringify({ ...result, ts: Date.now() }));
            } catch {}
        }
    }, [result, phase]);

    const reset = () => { setSize(null); setSvc(null); setPhase('idle'); };

    const openScheduleModal = () => {
        if (!result) return;
        const detailPrefix = result.customQuote
            ? `Commercial project · ${result.sz.label}. Please schedule a free on-site consultation.`
            : `Instant estimate: ${result.sv.label} for ${result.sz.label}. Ballpark $${fmt(result.range.low)}–$${fmt(result.range.high)}${result.range.plus ? '+' : ''}.`;
        navigate(`/estimate?service=${encodeURIComponent(result.sv.modalKey)}&details=${encodeURIComponent(detailPrefix)}`);
    };

    return (
        <section
            id="estimate"
            data-testid="msd-estimate"
            className="relative z-20 bg-ink-900 px-6 md:px-10 py-24 md:py-40 border-y border-white/10 overflow-hidden"
        >
            {/* Ambient glow */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{ background: 'radial-gradient(ellipse at 20% 20%, rgba(200,75,49,0.18) 0%, rgba(200,75,49,0) 55%)' }}
            />

            <div className="mx-auto max-w-[1600px] relative">
                <div className="flex items-center gap-4 mb-6">
                    <span className="w-10 h-px bg-adobe-500" />
                    <span className="eyebrow">Chapter 05 · Instant Estimate</span>
                </div>

                <div className="grid grid-cols-12 gap-8 md:gap-16 items-start">
                    {/* Left — question flow */}
                    <div className="col-span-12 md:col-span-7">
                        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-bone-100 max-w-2xl">
                            Two questions.
                            <br />
                            <span className="italic text-bone-300">A ballpark in seconds.</span>
                        </h2>
                        <p className="mt-6 max-w-lg font-body text-base md:text-lg font-light text-bone-300">
                            A realistic starting investment based on current New Mexico market rates — before your free on-site walkthrough.
                        </p>

                        {/* Q1: Size */}
                        <div className="mt-14">
                            <div className="flex items-baseline gap-4 mb-5">
                                <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-adobe-500">01</span>
                                <div className="font-display text-xl md:text-2xl text-bone-100">How large is your home?</div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {SIZES.map((s) => (
                                    <button
                                        key={s.id}
                                        data-testid={`estimate-size-${s.id}`}
                                        onClick={() => setSize(s.id)}
                                        className={`btn-pill ${size === s.id ? 'btn-pill--solid' : 'btn-pill--ghost'}`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Q2: Service */}
                        <AnimatePresence>
                            {size && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
                                    className="mt-12"
                                >
                                    <div className="flex items-baseline gap-4 mb-5">
                                        <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-adobe-500">02</span>
                                        <div className="font-display text-xl md:text-2xl text-bone-100">Which service?</div>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {SERVICES.map((s) => (
                                            <button
                                                key={s.id}
                                                data-testid={`estimate-svc-${s.id}`}
                                                onClick={() => setSvc(s.id)}
                                                className={`btn-pill ${svc === s.id ? 'btn-pill--solid' : 'btn-pill--ghost'}`}
                                            >
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Right — dynamic result card */}
                    <div className="col-span-12 md:col-span-5 md:pl-8 md:border-l md:border-white/10">
                        <AnimatePresence mode="wait">
                            {phase === 'idle' && (
                                <IdlePanel key="idle" />
                            )}
                            {phase === 'loading' && (
                                <LoadingPanel key="loading" msg={LOADING_MESSAGES[loadingMsgIdx]} />
                            )}
                            {phase === 'result' && result && (
                                <ResultCard
                                    key="result"
                                    result={result}
                                    onSchedule={openScheduleModal}
                                    onReset={reset}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const IdlePanel = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
    >
        <div className="eyebrow mb-4">Estimated Investment</div>
        <div
            className="font-display font-black leading-[0.9] tracking-[-0.03em] text-outline"
            style={{ fontSize: 'clamp(4rem, 10vw, 8rem)' }}
            data-testid="estimate-placeholder"
        >
            $ — —
        </div>
        <div className="mt-6 font-body text-sm text-bone-400 max-w-xs">
            Pick a size and a service — a realistic New Mexico ballpark will appear here.
        </div>
    </motion.div>
);

const LoadingPanel = ({ msg }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="estimate-loading"
    >
        <div className="eyebrow mb-6">Preparing your estimate</div>
        <div className="flex items-center gap-4 mb-8">
            <Loader2 size={22} className="text-adobe-500 animate-spin" strokeWidth={1.6} />
            <AnimatePresence mode="wait">
                <motion.div
                    key={msg}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="font-display italic text-lg md:text-xl text-bone-100"
                >
                    {msg}
                </motion.div>
            </AnimatePresence>
        </div>
        {/* skeleton bars */}
        <div className="space-y-3 max-w-sm">
            <div className="h-2.5 rounded-full bg-bone-500/15 overflow-hidden">
                <div className="h-full w-2/3 bg-adobe-500/60 animate-pulse" />
            </div>
            <div className="h-2.5 rounded-full bg-bone-500/15 overflow-hidden">
                <div className="h-full w-1/2 bg-adobe-500/40 animate-pulse" />
            </div>
            <div className="h-2.5 rounded-full bg-bone-500/15 overflow-hidden">
                <div className="h-full w-3/4 bg-adobe-500/50 animate-pulse" />
            </div>
        </div>
    </motion.div>
);

const ResultCard = ({ result, onSchedule, onReset }) => {
    const isCustom = !!result.customQuote;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.85, ease: [0.7, 0, 0.2, 1] }}
            data-testid="estimate-result"
            className="relative"
        >
            {/* Card */}
            <div className="relative rounded-[16px] bg-gradient-to-br from-ink-850 to-ink-800 border border-white/10 p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                <div className="flex items-center gap-2 mb-6">
                    <Sparkles size={12} className="text-adobe-500" strokeWidth={2} />
                    <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-adobe-500">
                        Your Estimated Investment
                    </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-6 mb-8 pb-8 border-b border-white/10">
                    <div>
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-bone-500 mb-1.5">Service</div>
                        <div className="font-display text-lg text-bone-100 leading-tight">{result.sv.label}</div>
                    </div>
                    <div>
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-bone-500 mb-1.5">Property Size</div>
                        <div className="font-display text-lg text-bone-100 leading-tight">{result.sz.label}</div>
                    </div>
                </div>

                {/* Price */}
                {isCustom ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mb-8"
                    >
                        <div className="font-display text-2xl md:text-[1.75rem] leading-[1.3] text-bone-100 max-w-md">
                            Commercial projects are <span className="italic text-adobe-500">custom quoted.</span>
                        </div>
                        <p className="mt-4 font-body text-sm text-bone-300 leading-relaxed max-w-md">
                            Pricing is based on size, scope, coatings, access requirements, and scheduling needs. Contact us to schedule a free on-site consultation.
                        </p>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: [0.7, 0, 0.2, 1] }}
                        className="mb-8"
                    >
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-bone-500 mb-2">Estimated Investment</div>
                        <div
                            className="font-display font-black text-bone-100 leading-[0.95] tracking-[-0.03em]"
                            style={{ fontSize: 'clamp(2.4rem, 5.6vw, 3.8rem)' }}
                        >
                            <span className="text-adobe-500">$</span>{fmt(result.range.low)}
                            <span className="mx-2 md:mx-3 text-bone-500 font-light">—</span>
                            <span className="text-adobe-500">$</span>{fmt(result.range.high)}{result.range.plus && '+'}
                        </div>
                    </motion.div>
                )}

                {/* Included list */}
                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="space-y-2.5 mb-8"
                >
                    {INCLUDED.map((item, i) => (
                        <motion.li
                            key={item}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: 0.35 + i * 0.06 }}
                            className="flex items-center gap-3 font-body text-[14px] text-bone-200"
                        >
                            <span className="w-5 h-5 rounded-full bg-adobe-500/15 flex items-center justify-center shrink-0">
                                <Check size={11} className="text-adobe-500" strokeWidth={2.5} />
                            </span>
                            {item}
                        </motion.li>
                    ))}
                </motion.ul>

                {/* CTA */}
                <motion.button
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.65 }}
                    type="button"
                    onClick={onSchedule}
                    data-testid="estimate-cta-call"
                    className="group w-full inline-flex items-center justify-between gap-3 py-4 px-6 rounded-full bg-bone-100 text-ink-900 font-body font-semibold text-[14px] tracking-[0.02em] uppercase hover:bg-adobe-500 hover:text-bone-100 transition-colors"
                >
                    Schedule My Free Estimate
                    <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
                </motion.button>

                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onReset}
                        data-testid="estimate-reset"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-500 hover:text-adobe-500"
                    >
                        <RotateCcw size={10} strokeWidth={1.75} />
                        Reset
                    </button>
                </div>
            </div>

            {/* Disclaimer */}
            <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.8 }}
                data-testid="estimate-disclaimer"
                className="mt-5 font-body text-[11.5px] leading-[1.65] text-bone-500 max-w-md"
            >
                {DISCLAIMER}
            </motion.p>
        </motion.div>
    );
};
